<template>
  <div>
    <div class="a-tab__title-block admin__a-tab admin__a-tab--modification-controls">
      <h3>{{$t('form.retend.lot-overview.title')}}</h3>
    </div>
    <overview-section :retend="retend" :lot="lot"></overview-section>

    <template v-if="canConfigureMyBestBidSection">
      <div class="a-tab__title-block admin__a-tab admin__a-tab--modification-controls">
        <h3>{{$t('form.retend.own-bid.title')}}</h3>
      </div>
      <offer-section :retend="retend"></offer-section>
    </template>

    <template v-if="canConfigureCompetitorBestBidSection">
      <div class="a-tab__title-block admin__a-tab admin__a-tab--modification-controls">
        <h3>{{$t('form.retend.competitor-best-bid.title')}}</h3>
      </div>
      <best-offer-section :retend="retend"></best-offer-section>
    </template>

    <div class="a-flex-start u-margin-top-24 a-width-100-percent" v-if="retend.permissions.CAN_APPROVE_RETEND">
      <button class="gui-btn gui-btn--red u-margin-right-20" :disabled="!retend.isFilled" @click.prevent="onSignRetendClick">{{$t('buttons.send-notice-about-retend')}}</button>
    </div>

    <div class="admin-buy__block-footer" ></div>

    <template v-if="retend.permissions.CAN_ACCEPT_RETEND">
      <div class="a-container-form">
        <div class="a-label a-label--w-280">{{$t('form.retend.status')}}:</div>

        <dropdown-new ref="requestState"
                      class="a-input-group select-w-260"
                      v-model="retend.state"
                      :options="retendStates">
        </dropdown-new>

        <button class="gui-btn gui-btn--red"
                @click.prevent="onAcceptRetendClick">{{$t('buttons.save')}}</button>
      </div>

      <div class="a-container-form" v-if="retend.state.valueOf() === enums.RetendState.DECLINED">
        <textarea class="a-textarea accreditation__textarea"
                  rows="4"
                  v-model="retend.reason"
                  placeholder="Причина отклонения">
        </textarea>
      </div>
    </template>

    <div class="admin-buy__block-gui-btn a-flex-start">
      <button class="gui-btn gui-btn--red gui-btn--w-167" type="button" v-if="retend.permissions.CAN_CANCEL_ACTIVE_RETEND" @click.prevent="onCancelActiveRetendClick">{{$t('buttons.cancel')}}</button>
      <button class="gui-btn gui-btn--red gui-btn--w-207" type="button" v-if="retend.permissions.CAN_EDIT_ACTIVE_RETEND" @click.prevent="onEditActiveRetendClick">{{$t('buttons.edit')}}</button>
    </div>

    <confirmation-dialog ref="confirmation" close-on-action>
    </confirmation-dialog>

    <signing-dialog ref="signRetend"
                    @signed="signRetend">
    </signing-dialog>

    <loading v-if="loading"></loading>
  </div>
</template>

<script>
  import OverviewSection from './sections/Overview'
  import OfferSection from './sections/Offer'
  import BestOfferSection from './sections/BestOffer'
  import SpecificationSection from './sections/Specification'
  import ConfigurationSection from './sections/Configuration'
  import Loading from '@/components/Loading'
  import Modal from '@/widgets/modal'
  import Dropdown from '@/widgets/dropdown'
  import SigningDialog from '@/components/common/dialogs/SigningDialog'
  import TemplateItem from "../../admin/catalogs/templates/TemplateItem";
  import ConfirmationDialog from '@/components/common/dialogs/ConfirmationDialog'

  export default {
    components: {
      TemplateItem,
      OverviewSection,
      OfferSection,
      BestOfferSection,
      SpecificationSection,
      ConfigurationSection,
      Loading,
      Modal,
      Dropdown,
      SigningDialog,
      ConfirmationDialog
    },

    props: {
      lot: {
        type: Object,
        required: true
      },

      retend: {
        type: Object,
        required: true
      }
    },

    computed: {
      canConfigureCompetitorBestBidSection() {
        return !this.lot.methods.actual.REDUCTION;
      },

      canConfigureMyBestBidSection() {
        return !this.lot.methods.actual.REDUCTION;
      },

      retendStates() {
        return [
          {
            id: this.enums.RetendState.NONE,
            name: this.enums.RetendState.label('NONE'),
            disabled: true
          },

          {
            id: this.enums.RequestState.REGISTERED,
            name: this.enums.RetendState.label('REGISTERED')
          },

          {
            id: this.enums.RequestState.DECLINED,
            name: this.enums.RetendState.label('DECLINED')
          }
        ]
      }
    },

    methods: {
      async onSignRetendClick() {
        const startDateTime = this.$options.filters.moment(this.retend.startTime, 'timezone', 'Europe/Minsk', 'DD.MM.YYYY HH:mm (\\G\\M\\TZ)')

        this.askConfirmation({
          title: 'Подписание переторжки',
          body: `Переторжка будет назначена на ${startDateTime}`,
          onConfirm: () => {
            this.runBlockerAction(async () => {
              try {
                const text = await this.$store.dispatch('getRetendSigningText', { lotId: this.lot.id });

                if (this.retend.permissions.SHOULD_BE_SIGNED) {
                  this.$refs.signRetend.open({text});
                }
                else {
                  this.signRetend();
                }
              }
              catch(e) {
                console.log('failed fetching signing text for retend', e);
                this.globalErrorHandler(e);
              }
            });
          }
        });
      },

      async signRetend(result) {
        this.runBlockerAction(async () => {
          try {
            this.$refs.signRetend.close();

            const willBeSentForApproval = this.retend.willBeSentForApproval;

            await this.$store.dispatch('signRetend', {
              lotId: this.lot.id,
              text: result ? result.data : undefined,
              signature: result ? result.signature : undefined
            });

            this.showError({
              title: 'Извещение о переторжке',
              error: (willBeSentForApproval ? 'Извещение о переторже отправлено на утверждение Главному организатору' : 'Измещение о переторже опубликовано')
            });
          }
          catch(e) {
            console.log('failed signing retend', e);
            this.globalErrorHandler(e);
          }
        });
      },

      async onAcceptRetendClick() {
        this.runBlockerAction(async () => {
          try {
            await this.$store.dispatch('acceptRetend', {
              lotId: this.lot.id,
              accepted: this.retend.state.valueOf() === this.enums.RetendState.REGISTERED,
              reason: this.retend.reason
            });
          }
          catch(e) {
            console.log('failed accepting retend', e);
            this.globalErrorHandler(e);
          }
        });
      },

      onCancelActiveRetendClick() {
        this.$refs.confirmation.open({
          title: 'Отмена переторжки',
          text: 'Вы хотите отменить назначенную переторжку?',
          onConfirm: () => {
            this.runBlockerAction(async () => {
              try {
                await this.$store.dispatch('cancelActiveRetend', {
                  lotId: this.lot.id
                });

                this.showError({
                  title: 'Удаление переторжки',
                  error: 'Переторжка удалена'
                });

                await this.$router.replace({
                  name: 'purchases-my-lots-details',
                  params: {
                    lotId: this.lot.id,
                    tab: 'overview'
                  }
                })
              }
              catch(e) {
                console.log('failed cancelling active retend', e);
                this.globalErrorHandler(e);
              }
            });
          }
        });
      },

      onEditActiveRetendClick() {
        this.$refs.confirmation.open({
          title: 'Изменение переторжки',
          text: 'Вы хотите изменить данные переторжки?',
          onConfirm: () => {
            this.runBlockerAction(async () => {
              try {
                await this.$store.dispatch('editActiveRetend', {
                  lotId: this.lot.id
                });
              }
              catch(e) {
                console.log('failed enabling active retend editing', e);
                this.globalErrorHandler(e);
              }
            });
          }
        });
      }
    }
  }
</script>

<style scoped lang="scss">
</style>
